import * as z from 'zod';
import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_GESTIFY_CREDENTIALS,
  MN_USERS,
  MODULE,
} from '../common';
import type { Prisma } from '@prisma/client';
import type { KeyValue } from '@jobcost-monorepo/utils';

type GestifyCredential = Prisma.$GestifyCredentialPayload['scalars'];

export type IGestifyCredential = GestifyCredential &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    user: KeyValue;
  };

export interface IListGestifyCredentialFetchParams extends IListFetchParams {
  userId?: number;
}

class GestifyCredentialRepository extends BaseRepository<
  IGestifyCredential,
  IListGestifyCredentialFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['username'],
    };
    this.gridFieldsOptions = [
      {
        visible: false,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'username',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'user.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'userId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_USERS,
        translationModuleName: MN_GESTIFY_CREDENTIALS,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_GESTIFY_CREDENTIALS,
        translationModuleName: MN_GESTIFY_CREDENTIALS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'username',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_GESTIFY_CREDENTIALS,
        translationModuleName: MN_GESTIFY_CREDENTIALS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'password',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_GESTIFY_CREDENTIALS,
        translationModuleName: MN_GESTIFY_CREDENTIALS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'userId',
        linkedFieldForDefaultValue: 'user.name',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_USERS,
        translationModuleName: MN_GESTIFY_CREDENTIALS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'note',
        type: 'string',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_GESTIFY_CREDENTIALS,
        translationModuleName: MN_GESTIFY_CREDENTIALS,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      username: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_255' }),
      password: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_255' }),
      userId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_255' })
        .nullable()
        .optional(),
    });
  }
}

export default GestifyCredentialRepository;
